import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Video, Mic2 } from 'lucide-react';
import { CalendarWeekStrip } from 'shared-ui';
import * as servicesApi from '../services/servicesService';
import { useToast } from '../context/ToastContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import PWAInstallPrompt from '../components/features/PWAInstallPrompt';

/** Monday–Sunday ISO range for calendar preview (local calendar math). */
function weekRangeISO(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon.toISOString(), end: sun.toISOString() };
}

const DELIVERY_OPTIONS = [
  { id: 'in_person', label: 'In-person', sublabel: 'Sunshine Coast', icon: MapPin },
  { id: 'video', label: 'Video call', sublabel: 'Online meeting', icon: Video },
  { id: 'voice', label: 'Voice call', sublabel: 'Phone / audio', icon: Mic2 },
];

const formatSlot = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateForDisplay = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDualTime = (iso) => {
  const d = new Date(iso);
  const aest = d.toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const local = d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time';
  return { aest: `${aest} (AEST, Sunshine Coast)`, local: `${local} (${tzName})` };
};

const BookConsultation = ({ embedded = false }) => {
  const location = useLocation();
  const { showToast } = useToast();
  const {
    customer,
    isAuthenticated,
    hasMangoPersonalCalendar,
    getAuthHeaders,
    getSignInWithMangoUrl,
  } = useCustomerAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    reason: '',
    notes: '',
  });
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [slotsNote, setSlotsNote] = useState(null);
  const [previewEvents, setPreviewEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    servicesApi.getServices({ serviceType: 'bookable_individual' })
      .then((list) => {
        if (!cancelled) {
          const supportServices = list.filter((s) => Number(s.duration || 0) > 0 && Number(s.duration) <= 15 && s.category !== 'guidance_call');
          setServices(supportServices);
          if (supportServices.length > 0) setSelectedService((prev) => prev || supportServices[0]);
        }
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAuthenticated && customer) {
      const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() || customer.email?.split('@')[0] || '';
      setForm(f => ({
        ...f,
        customerName: f.customerName || name,
        customerEmail: f.customerEmail || customer.email || '',
      }));
    }
  }, [isAuthenticated, customer]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reasonParam = (params.get('reason') || '').trim();
    const summaryParam = (params.get('summary') || '').trim();
    if (!reasonParam && !summaryParam) return;
    setForm((f) => ({
      ...f,
      reason: f.reason || reasonParam,
      notes: f.notes || summaryParam,
    }));
  }, [location.search]);

  useEffect(() => {
    if (!selectedService?.id || !selectedDate) {
      setSlots([]);
      setSlotsNote(null);
      return;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setSlotsNote(null);
    const headers = getAuthHeaders();
    const useMe = hasMangoPersonalCalendar && headers.Authorization;

    (async () => {
      try {
        if (useMe) {
          const data = await servicesApi.fetchAvailableSlotsForMe(
            selectedService.id,
            selectedDate,
            undefined,
            headers
          );
          if (cancelled) return;
          setSlots(data.slots || []);
          if (data.filteredByPersonalCalendar && data.personalSlotsDropped > 0) {
            setSlotsNote(
              `We hid ${data.personalSlotsDropped} time(s) that overlap your Mango personal calendar.`
            );
          } else if (data.hasPersonalCalendar) {
            setSlotsNote('Showing times that fit your Mango calendar.');
          }
        } else {
          const list = await servicesApi.getAvailableSlots(selectedService.id, selectedDate);
          if (cancelled) return;
          setSlots(list);
        }
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedService?.id, selectedDate, hasMangoPersonalCalendar, getAuthHeaders]);

  useEffect(() => {
    if (!hasMangoPersonalCalendar || !selectedDate) {
      setPreviewEvents([]);
      return;
    }
    let cancelled = false;
    const { start, end } = weekRangeISO(selectedDate);
    servicesApi
      .fetchCalendarPreview(start, end, getAuthHeaders())
      .then((d) => {
        if (!cancelled) setPreviewEvents(Array.isArray(d.events) ? d.events : []);
      })
      .catch(() => {
        if (!cancelled) setPreviewEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, hasMangoPersonalCalendar, getAuthHeaders]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const startTime = e.target?.startTime?.value;
    if (!selectedService?.id || !startTime || !deliveryMethod || !form.customerName?.trim() || !form.customerEmail?.trim() || !form.reason?.trim()) {
      setError('Please select a service, date, time, delivery method, and fill in name, email, and call reason.');
      return;
    }
    if (deliveryMethod === 'voice' && !form.customerPhone?.trim()) {
      setError('Phone number is required for voice calls.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const deliveryLabel = DELIVERY_OPTIONS.find(o => o.id === deliveryMethod);
      const methodText = deliveryLabel ? (deliveryLabel.sublabel ? `${deliveryLabel.label} (${deliveryLabel.sublabel})` : deliveryLabel.label) : deliveryMethod;
      const reasonText = form.reason.trim();
      const summaryText = form.notes?.trim();
      const supportNotes = summaryText
        ? `Support reason: ${reasonText}\nAI call plan:\n${summaryText}`
        : `Support reason: ${reasonText}`;
      const data = await servicesApi.createPublicBooking({
        serviceId: selectedService.id,
        startTime,
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone?.trim() || undefined,
        deliveryMethod: methodText,
        notes: supportNotes,
        _hp: honeypot,
        authHeaders: hasMangoPersonalCalendar ? getAuthHeaders() : {},
      });
      setSuccessData({
        bookingNumber: data.bookingNumber || data.booking?.bookingNumber,
        startTimeISO: data.startTimeISO || data.booking?.startTime,
        startTimeAEST: data.startTimeAEST,
      });
      setSuccess(true);
      showToast('Support call booked!', 'success');
    } catch (err) {
      setError(err.message || 'Booking failed');
      showToast(err.message || 'Booking failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date().toISOString().slice(0, 10);
  const maxDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 2);
    return d.toISOString().slice(0, 10);
  })();

  const pageStyle = embedded
    ? {
        minHeight: 0,
        position: 'relative',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        color: 'white',
        overflow: 'visible',
        borderRadius: 12,
        border: '1px solid rgba(16, 185, 129, 0.35)',
      }
    : {
        minHeight: '100vh',
        position: 'relative',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        color: 'white',
        overflow: 'hidden',
      };

  const containerStyle = {
    position: 'relative',
    zIndex: 10,
    padding: embedded
      ? '16px 14px 20px'
      : typeof window !== 'undefined' && window.innerWidth <= 768
        ? '20px 16px'
        : '20px',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    maxWidth: embedded ? 'none' : '1280px',
    margin: '0 auto',
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
    backdropFilter: 'blur(4px)',
    borderRadius: '12px',
    padding: '32px',
    border: '2px solid #10b981',
    marginBottom: '24px',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid rgba(16, 185, 129, 0.5)',
    background: 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle = { display: 'block', marginBottom: '8px', color: '#4ade80', fontWeight: 600 };
  const btnStyle = {
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    padding: '14px 28px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    width: '100%',
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...containerStyle, textAlign: 'center', paddingTop: embedded ? '24px' : '80px' }}>
          <p style={{ color: '#22d3ee', fontSize: embedded ? '1rem' : '1.25rem' }}>Loading services…</p>
        </div>
      </div>
    );
  }

  if (success) {
    const dual = successData?.startTimeISO ? formatDualTime(successData.startTimeISO) : null;
    const bookAgainTo = embedded ? '/customer/profile?tab=support' : '/book-consultation';
    return (
      <div style={pageStyle}>
        {!embedded && <PWAInstallPrompt />}
        <div style={containerStyle}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ ...cardStyle, textAlign: 'center' }}
          >
            <h2 style={{ color: '#10b981', marginBottom: '16px' }}>Booking confirmed</h2>
            {successData?.bookingNumber && (
              <p style={{ color: '#4ade80', fontWeight: 600, marginBottom: '12px' }}>Reference: {successData.bookingNumber}</p>
            )}
            {dual && (
              <div style={{ marginBottom: '16px', textAlign: 'left', maxWidth: '400px', margin: '0 auto 16px' }}>
                <p style={{ color: '#d1d5db', margin: '4px 0' }}><strong>My time (Sunshine Coast):</strong> {dual.aest}</p>
                <p style={{ color: '#22d3ee', margin: '4px 0' }}><strong>Your time:</strong> {dual.local}</p>
              </div>
            )}
            <p style={{ color: '#d1d5db', lineHeight: 1.6, marginBottom: '24px' }}>
              Your customer support call has been booked. You’ll receive a confirmation email with details and the recording once the session is complete.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <Link to={bookAgainTo} onClick={() => { setSuccess(false); setSuccessData(null); }} style={{ ...btnStyle, textDecoration: 'none', display: 'inline-block', width: 'auto', padding: '12px 24px' }}>Book another support call</Link>
              <Link to={embedded ? '/customer/profile?tab=support' : '/'} style={{ ...btnStyle, background: 'rgba(16, 185, 129, 0.5)', textDecoration: 'none', display: 'inline-block', width: 'auto', padding: '12px 24px' }}>{embedded ? 'Back to account' : 'Back to home'}</Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {!embedded && <PWAInstallPrompt />}
      <div style={containerStyle}>
        {!embedded && (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              style={{ textAlign: 'center', marginBottom: '24px', paddingTop: '20px' }}
            >
              <img src="/favicon.png" alt="Rainbow Sails" style={{ width: '25%', maxWidth: '200px', height: 'auto', margin: '0 auto' }} />
            </motion.div>

            <header style={{ textAlign: 'center', marginBottom: '48px' }}>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ fontSize: 'clamp(2rem, 6vw, 3.5rem)', fontWeight: 'bold', marginBottom: '16px', color: '#10b981' }}
              >
                Book Customer Support Call
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', color: '#22d3ee' }}
              >
                In-person • Video • Voice • Max 15 minutes • Recording shared with you
              </motion.p>
            </header>
          </>
        )}
        {embedded && (
          <header style={{ textAlign: 'left', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: '0 0 8px', color: '#34d399' }}>
              Book a support call
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#a5b4fc', margin: 0, lineHeight: 1.5 }}>
              In-person, video, or voice — up to 15 minutes. The call is recorded and shared with you.
            </p>
          </header>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ ...cardStyle, borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.2)' }}
          >
            <p style={{ color: '#fca5a5', margin: 0 }}>{error}</p>
          </motion.div>
        )}

        {services.length === 0 && !loading ? (
          <motion.div style={cardStyle}>
            <p style={{ color: '#f59e0b' }}>No active 15-minute support services are available at the moment. Please check back later or contact us.</p>
          </motion.div>
        ) : (
        <form onSubmit={handleSubmit} aria-label="Book a customer support call">
          {/* Honeypot - hidden from users, bots fill it */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
            <label htmlFor="booking-hp">Leave blank</label>
            <input id="booking-hp" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
          </div>
          {/* Recording notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ ...cardStyle, borderColor: '#a78bfa' }}
          >
            <p style={{ color: '#e9d5ff', fontSize: '1rem', margin: 0 }}>
              The support call will be recorded and shared with you.
            </p>
          </motion.div>

          {/* Service details */}
          {selectedService && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.33 }} style={cardStyle}>
              <h3 style={{ color: '#10b981', marginBottom: '8px' }}>{selectedService.name}</h3>
              <p style={{ color: '#d1d5db', margin: 0 }}>
                {Math.min(selectedService.duration || 15, 15)} min
                {selectedService.price != null && selectedService.price > 0 && (
                  <span style={{ marginLeft: '12px', color: '#4ade80' }}>
                    • ${Number(selectedService.price).toFixed(2)}
                  </span>
                )}
              </p>
            </motion.div>
          )}

          {/* Service selection (only if multiple) */}
          {services.length > 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} style={cardStyle}>
              <label style={labelStyle}>Choose service</label>
              <select
                value={selectedService?.id || ''}
                onChange={(e) => setSelectedService(services.find(s => s.id === e.target.value))}
                style={inputStyle}
              >
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} — {Math.min(s.duration || 15, 15)} min</option>
                ))}
              </select>
            </motion.div>
          )}

          {/* Delivery method */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} style={cardStyle} role="group" aria-label="Delivery method">
            <label style={labelStyle}>How would you like to meet?</label>
            {deliveryMethod === 'in_person' && (
              <p style={{ color: '#a78bfa', fontSize: '0.9rem', marginBottom: '12px' }}>
                Location: Sunshine Coast, QLD — exact address and directions in your confirmation email.
              </p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {DELIVERY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = deliveryMethod === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDeliveryMethod(opt.id)}
                    aria-pressed={isSelected}
                    aria-label={`${opt.label} - ${opt.sublabel}`}
                    style={{
                      flex: '1 1 140px',
                      padding: '16px',
                      borderRadius: '8px',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(16, 185, 129, 0.5)'}`,
                      background: isSelected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 0, 0, 0.4)',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Icon size={24} />
                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                    <span style={{ fontSize: '0.85rem', color: '#22d3ee' }}>{opt.sublabel}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Date & time */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} style={cardStyle}>
            {!hasMangoPersonalCalendar && (
              <p style={{ color: '#fbbf24', fontSize: '0.9rem', marginBottom: '12px' }}>
                <a href={getSignInWithMangoUrl()} style={{ color: '#fde68a', fontWeight: 600 }}>
                  Sign in with Mango
                </a>{' '}
                to hide times that clash with your personal calendar and preview your week.
              </p>
            )}
            <p style={{ color: '#a78bfa', fontSize: '0.9rem', marginBottom: '16px' }} aria-live="polite">
              Times shown in AEST (Sunshine Coast). Your booking will be displayed in your timezone too.
            </p>
            <label style={labelStyle} htmlFor="booking-date">Date</label>
            <input
              id="booking-date"
              type="date"
              min={minDate}
              max={maxDate}
              value={selectedDate}
              onChange={(e) => { setSelectedDate(e.target.value); setSelectedSlot(null); }}
              style={inputStyle}
              required
              aria-required="true"
            />
            <label style={{ ...labelStyle, marginTop: '20px' }} htmlFor="booking-time">Time</label>
            {slotsNote && (
              <p style={{ color: '#86efac', fontSize: '0.9rem', marginBottom: '12px' }}>{slotsNote}</p>
            )}
            {loadingSlots && selectedDate && <p style={{ color: '#22d3ee' }}>Loading slots…</p>}
            <select
              id="booking-time"
              name="startTime"
              required
              style={inputStyle}
              disabled={!selectedDate || loadingSlots}
              onChange={(e) => setSelectedSlot(e.target.value || null)}
              aria-required="true"
            >
              <option value="">Select a time</option>
              {slots.map((s) => (
                <option key={s} value={s}>{formatSlot(s)} (AEST)</option>
              ))}
            </select>
            {selectedSlot && (() => {
              const dual = formatDualTime(selectedSlot);
              return (
                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                  <p style={{ color: '#4ade80', margin: '4px 0', fontSize: '0.9rem' }}><strong>My time (Sunshine Coast):</strong> {dual.aest}</p>
                  <p style={{ color: '#22d3ee', margin: '4px 0', fontSize: '0.9rem' }}><strong>Your time:</strong> {dual.local}</p>
                </div>
              );
            })()}
            {selectedDate && !loadingSlots && slots.length === 0 && (
              <p style={{ color: '#f59e0b', marginTop: '8px' }}>No slots available on {formatDateForDisplay(selectedDate)}</p>
            )}
            {hasMangoPersonalCalendar && selectedDate && (
              <div style={{ marginTop: '20px' }}>
                <p style={{ color: '#4ade80', fontWeight: 600, marginBottom: '4px' }}>Your week (Mango calendar)</p>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '8px' }}>
                  Proposed booking appears as &quot;+ proposed&quot; on that day.
                </p>
                <CalendarWeekStrip
                  anchorDate={new Date(`${selectedDate}T12:00:00`)}
                  events={previewEvents}
                  ghost={
                    selectedSlot
                      ? {
                          startTime: selectedSlot,
                          endTime: new Date(
                            new Date(selectedSlot).getTime() +
                              Math.min(selectedService?.duration || 15, 15) * 60 * 1000
                          ).toISOString(),
                          title: selectedService?.name || 'Booking',
                        }
                      : null
                  }
                  selectedDayKey={selectedDate}
                />
              </div>
            )}
          </motion.div>

          {/* Contact details */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={cardStyle}>
            <h3 style={{ color: '#10b981', marginBottom: '16px' }}>Your details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle} htmlFor="booking-name">Name *</label>
                <input
                  id="booking-name"
                  type="text"
                  value={form.customerName}
                  onChange={(e) => setForm(f => ({ ...f, customerName: e.target.value }))}
                  placeholder="Your full name"
                  style={inputStyle}
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="booking-email">Email *</label>
                <input
                  id="booking-email"
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                  placeholder="your@email.com"
                  style={inputStyle}
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="booking-phone">
                  Phone {deliveryMethod === 'voice' ? '*' : '(optional)'}
                </label>
                {deliveryMethod === 'voice' && (
                  <p style={{ color: '#f59e0b', fontSize: '0.9rem', margin: '-4px 0 8px' }}>Required for voice calls — we’ll call you at this number.</p>
                )}
                <input
                  id="booking-phone"
                  type="tel"
                  value={form.customerPhone}
                  onChange={(e) => setForm(f => ({ ...f, customerPhone: e.target.value }))}
                  placeholder="For callbacks"
                  style={inputStyle}
                  required={deliveryMethod === 'voice'}
                  aria-required={deliveryMethod === 'voice'}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="booking-reason">Reason for call *</label>
                <textarea
                  id="booking-reason"
                  value={form.reason}
                  onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Tell us what you need help with."
                  style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }}
                  rows={3}
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="booking-notes">Notes (optional)</label>
                <textarea
                  id="booking-notes"
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional AI-confirmed call plan or extra context..."
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  rows={3}
                />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
            <button type="submit" disabled={submitting} style={btnStyle}>
              {submitting ? 'Booking…' : 'Confirm booking'}
            </button>
          </motion.div>
        </form>
        )}
      </div>
    </div>
  );
};

export default BookConsultation;
